import { ProjectDTO } from '../dtos/index';
import { ProjectsTable } from '../views/index';
import { DATE_MDY } from '../../utils/date/index';
import { IS_NOT_NULL_OR_UNDEFINED } from '../../utils/commonUtils/validation-utils';
import { LOAD_VOLUME_DURATION, PROJECT_POSITIONS } from '../constants';
import { FormControl, Validators } from '@angular/forms';
import { IS_EMPTY, IS_NOT_EMPTY, TITLE_CASE, TO_DOLLARS } from '../../utils';
import { GET_LANE_PATH_STRING } from './get-lane-path-string';

export const BROKER_PROJECTS_TABLE = (
	projectDTOList: Array<ProjectDTO> = [],
	isAnnualVolumeEnabled: boolean = false,
	userId: number = null,
	usersList: Array<any> = []
): Array<ProjectsTable> => {
	let results: Array<ProjectsTable> = [];
	try {
		if (projectDTOList.length > 0) {
			projectDTOList.map((projectDTO: ProjectDTO) => {
				let project = {} as ProjectsTable;
				project.name = projectDTO.name;
				project.dueDate = DATE_MDY(projectDTO.dueDate);
				project.carrierCount = projectDTO.carrierCount;
				project.id = projectDTO.id;
				project.userId = projectDTO.userId;
				project.assigneeId = projectDTO.assigneeId;
				project.assignee = projectDTO.assignee;
				project.customerDTO = projectDTO.customerDTO;

				project.minRate =
					IS_NOT_NULL_OR_UNDEFINED(projectDTO.minRate) && projectDTO.minRate > 0
						? TO_DOLLARS(projectDTO.minRate)
						: null;
				project.maxRate =
					IS_NOT_NULL_OR_UNDEFINED(projectDTO.maxRate) && projectDTO.maxRate > 0
						? TO_DOLLARS(projectDTO.maxRate)
						: null;
				project.maxBuy =
					IS_NOT_NULL_OR_UNDEFINED(projectDTO.maxBuy) && projectDTO.maxBuy > 0
						? TO_DOLLARS(projectDTO.maxBuy)
						: null;
				project.value =
					IS_NOT_NULL_OR_UNDEFINED(projectDTO.value) && projectDTO.value > 0
						? TO_DOLLARS(projectDTO.value)
						: null;

				project.commodity = projectDTO.commodity;
				project.loadVolume = projectDTO.loadVolume;
				project.daysRun = GET_LOAD_VOLUME(projectDTO);
				project.packaging = projectDTO.packaging;
				project.trailerRequirements = projectDTO.trailerRequirements;
				project.shipperNotes = projectDTO.shipperNotes;
				project.receiverNotes = projectDTO.receiverNotes;
				project.lanePath = GET_LANE_PATH_STRING(projectDTO);
				project.taskCount = projectDTO.taskCount;

				project.loadCapacity = projectDTO.loadCapacity;
				project.loadCount = projectDTO.loadCount;
				project.stageId = projectDTO.stageId;
				project.loadVolumeDuration = projectDTO.loadVolumeDuration;
				project.position = PROJECT_POSITIONS[projectDTO.position];

				project.monday = projectDTO.monday;
				project.tuesday = projectDTO.tuesday;
				project.wednesday = projectDTO.wednesday;
				project.thursday = projectDTO.thursday;
				project.friday = projectDTO.friday;
				project.saturday = projectDTO.saturday;
				project.sunday = projectDTO.sunday;
				project.selectedDaysRun = project.daysRun.split(', ');
				project.dueDateFormControl = new FormControl(
					new Date(DATE_MDY(projectDTO.dueDate)),
					Validators.required
				);
				project.lastBid = projectDTO.lastBid;
				project.bestRate = projectDTO.bestRate;
				project.probability = projectDTO.probability;
				project.isAssigneeEnabled = false;
				if (IS_NOT_NULL_OR_UNDEFINED(userId)) {
					project.isAssigneeEnabled = userId == projectDTO.userId;
				}
				project.assigneePlaceholder = 'Unassigned';
				project.selectedAssigneeIds = IS_NOT_NULL_OR_UNDEFINED(projectDTO.assigneeId)
					? [projectDTO.assigneeId]
					: [];

				if (
					IS_NOT_NULL_OR_UNDEFINED(projectDTO.minRate) &&
					IS_NOT_NULL_OR_UNDEFINED(projectDTO.maxRate)
				) {
					project.targetMargin = Math.round(
						Number(((project.maxRate - project.minRate) / project.maxRate) * 100)
					);
				}

				const annualVolume = ANNUAL_VOLUME_DISPLAY(projectDTO);
				if (isAnnualVolumeEnabled) {
					if (
						IS_NOT_NULL_OR_UNDEFINED(projectDTO.loadVolume) &&
						IS_NOT_NULL_OR_UNDEFINED(projectDTO.loadVolumeDuration)
					) {
						project.annualVolumeDisplay = annualVolume;
						project.annualVolumeTooltip = `${projectDTO.loadVolume}/${projectDTO.loadVolumeDuration}`;
						project.opportunityValue = OPPORTUNITY_VALUE(projectDTO, project.annualVolumeDisplay);
					} else {
						project.annualVolumeDisplay = '';
						project.annualVolumeTooltip = '';
					}
				}
				setShowProjectname(project);

				if (
					IS_NOT_NULL_OR_UNDEFINED(userId) &&
					IS_NOT_NULL_OR_UNDEFINED(usersList) &&
					usersList.length > 0
				) {
					const user = usersList.find((user) => user.userId == projectDTO.userId);
					project.ownerName = IS_NOT_NULL_OR_UNDEFINED(user) ? user.name : '';
				}

				if (
					IS_NOT_NULL_OR_UNDEFINED(userId) &&
					IS_NOT_NULL_OR_UNDEFINED(usersList) &&
					usersList.length > 0
				) {
					project.isAssigneeEnabled = userId == project.userId;
					if (!project.isAssigneeEnabled) {
						project.usersList = [];
						if (IS_NOT_NULL_OR_UNDEFINED(projectDTO.assigneeId)) {
							const user = usersList.find((user) => user.id == projectDTO.assigneeId);
							usersList.map((user) => {
								if (project.selectedAssigneeIds.indexOf(user.userId) > -1) {
									project.assigneePlaceholder = user.name + ' ';
								}
							});
						}
					} else {
						project.usersList = usersList.map((user) => {
							let userCopy: any = Object.assign({}, user);
							if (project.selectedAssigneeIds.indexOf(user.userId) > -1) {
								project.assigneePlaceholder = user.name + ' ';
								userCopy.selected = true;
							}
							return userCopy;
						});
					}
				}

				project.weight = projectDTO.weight;
				if (IS_NOT_NULL_OR_UNDEFINED(annualVolume)) {
					project.monthlyVolumeDisplay = `${Math.round(annualVolume / 12)}/Month`;
				}
				project.type = projectDTO.type;

				results.push(project);
			});
		}
	} catch (error) {
		console.log('BROKER_PROJECTS_TABLE error: ', error);
	}

	return results;
};

export const GET_LOAD_VOLUME = (projectDTO: ProjectDTO): string => {
	let result = '';
	if (
		projectDTO.monday ||
		projectDTO.tuesday ||
		projectDTO.wednesday ||
		projectDTO.thursday ||
		projectDTO.friday ||
		projectDTO.saturday ||
		projectDTO.sunday
	) {
		let daysOfWeek = [];
		if (projectDTO.monday) {
			daysOfWeek.push('M');
		}
		if (projectDTO.tuesday) {
			daysOfWeek.push('T');
		}
		if (projectDTO.wednesday) {
			daysOfWeek.push('W');
		}
		if (projectDTO.thursday) {
			daysOfWeek.push('TH');
		}
		if (projectDTO.friday) {
			daysOfWeek.push('F');
		}
		if (projectDTO.saturday) {
			daysOfWeek.push('SAT');
		}
		if (projectDTO.sunday) {
			daysOfWeek.push('SUN');
		}
		if (daysOfWeek.length > 0) {
			result = daysOfWeek.join(', ');
		}
	}
	return result;
};

export const UPDATE_PROJECT_BY_USERID = (
	projectList: Array<ProjectsTable>,
	userId: number
): Array<ProjectsTable> => {
	if (projectList.length > 0) {
		projectList.map((project: ProjectsTable) => {
			project.isAssigneeEnabled = userId == project.userId;
			if (IS_NOT_NULL_OR_UNDEFINED(project.assigneeId) && userId == project.userId) {
				project.assigneePlaceholder = 'Assign User';
			}
		});
	}
	return projectList;
};

export const ANNUAL_VOLUME_DISPLAY = (projectDTO: ProjectDTO): any => {
	switch (projectDTO.loadVolumeDuration?.toLowerCase()) {
		case LOAD_VOLUME_DURATION.DAY.toLowerCase():
			return projectDTO.loadVolume * 365;
		case LOAD_VOLUME_DURATION.WEEK.toLowerCase():
			return projectDTO.loadVolume * 52;
		case LOAD_VOLUME_DURATION.MONTH.toLowerCase():
			return projectDTO.loadVolume * 12;
		case LOAD_VOLUME_DURATION.YEAR.toLowerCase():
			return projectDTO.loadVolume;
		default:
			return null;
	}
};

export const OPPORTUNITY_VALUE = (projectDTO: ProjectDTO, annualVolumeDisplay: any): any => {
	if (
		IS_NOT_NULL_OR_UNDEFINED(projectDTO.maxRate) &&
		IS_NOT_NULL_OR_UNDEFINED(annualVolumeDisplay)
	) {
		return Math.abs(annualVolumeDisplay * (projectDTO.maxRate / 100));
	}
	return '';
};

const setShowProjectname = (project: ProjectsTable): any => {
	if (IS_EMPTY(project.lanePath)) {
		project.showProjectName = project.isProjectNameDisplayed = true;
		return;
	}
	project.showProjectName = project.isProjectNameDisplayed = false;
	if (project.lanePath == project.name) {
		return;
	}
	if (
		IS_NOT_NULL_OR_UNDEFINED(project.customerDTO) &&
		project.customerDTO.name.toLowerCase() == project.name.toLowerCase()
	) {
		return;
	}
	project.showProjectName = project.isProjectNameDisplayed = true;
};
